/**
 * TrainingLocationsSection — Choose Your Training Location.
 *
 * Homepage section placed between Browse by Age Group and Online Training
 * Plan (Feb 2026 spec). Surfaces the four physical training hubs LGRA runs
 * at, plus a fifth "Request a Class" card that opens Jason's WhatsApp so
 * visitors can quickly enquire about training at a different location.
 *
 * Each hub card carries:
 *   • Subtitle (audience-first, e.g. "Our Main Training Hub")
 *   • 3-5 programme bullets so visitors know what actually runs there
 *   • CTA linking to /schedule?location=<slug> — the Schedule page reads the
 *     query param, sets the location filter, and remembers it via
 *     localStorage so returning to the schedule from a booking doesn't lose
 *     the picked location.
 *
 * Admin-editing of the location list is a future task — for now the config
 * lives inline so the section ships without a new admin surface.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { MapPin, ArrowRight, Check, MessageCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Some image URLs stored server-side may be RELATIVE (e.g. admin-uploaded
// files served via /api/public/location-image-file/...). Prefix them with
// the backend URL so <img src> works. Absolute http(s) URLs pass through.
const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
};

// Direct WhatsApp link for the "Other Locations" enquiry. We swap to the
// full wa.me endpoint so we can pre-fill the message body — the phone number
// is Jason's LGRA number (+60 16-255 4068, same as other footer links).
const OTHER_LOCATION_WA_MESSAGE = `Hi LGRA, I would like to request a class at another location.
Preferred location:
Class type:
Number of participants:
Preferred date and time:`;
const OTHER_LOCATION_WA_LINK = `https://wa.me/60162554068?text=${encodeURIComponent(OTHER_LOCATION_WA_MESSAGE)}`;

const LOCATIONS = [
  {
    slug: 'bukit_jalil',
    name: 'Bukit Jalil',
    // Feb 2026 spec: subtitles describe the location's *character*, not
    // a specific day of the week. Bukit Jalil is our flagship / widest
    // selection hub — flagged with `isHub` so the card gets a subtle
    // "Main Hub" highlight while sharing the same base design.
    subtitle: 'LGRA Main Training Hub',
    isHub: true,
    // Free-to-use imagery from Unsplash. Swap for admin-uploaded images once
    // the admin location editor lands.
    image: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80',
    programmes: [
      'Adult Running Classes',
      'Kids & Teens Classes',
      'Community Classes',
      'Strength & Performance Training',
      'Private Coaching',
    ],
    cta: 'Explore Bukit Jalil',
  },
  {
    slug: 'alam_impian',
    name: 'Alam Impian',
    // "Beginner-Friendly" (rather than "Beginner Class") keeps the door open
    // to experienced runners looking for a supportive base group.
    subtitle: 'Beginner-Friendly Training Location',
    image: 'https://images.unsplash.com/photo-1461896836934-ffe607ba8211?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80',
    programmes: [
      'Beginner Running Classes',
      'Adult Running Classes',
      'Kids & Teens Classes',
      'Supportive Community Training',
    ],
    cta: 'Explore Alam Impian',
  },
  {
    slug: 'lake_gardens',
    name: 'Lake Gardens KL',
    // Subtitle must NOT imply "kids only" — adults train here too.
    subtitle: 'Running & Endurance Location',
    image: 'https://images.unsplash.com/photo-1526676037777-05a232554d77?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80',
    programmes: [
      'Adult Running Classes',
      'Kids & Teens Classes',
      'Running Skills & Endurance',
      'Private Coaching',
    ],
    cta: 'Explore Lake Gardens',
  },
  {
    slug: 'putrajaya',
    name: 'Putrajaya',
    subtitle: 'Programmes & Performance Location',
    image: 'https://images.unsplash.com/photo-1476480862126-209bfaa8edc8?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80',
    programmes: [
      'Running Programmes',
      'Hill & Endurance Training',
      'Specific-Topic Classes',
      'Private Coaching by Request',
    ],
    cta: 'Explore Putrajaya',
  },
];

// Prime the Schedule page's location filter via URL param + localStorage so
// the choice sticks when the visitor moves on to booking then back.
const rememberLocation = (slug) => {
  try {
    localStorage.setItem('lgra:selected_location', slug);
  } catch { /* localStorage disabled — fail silently */ }
};

const LocationCard = ({ loc, imageUrl }) => (
  <div
    className={`group relative overflow-hidden rounded-2xl bg-white border-2 hover:shadow-xl transition-all flex flex-col ${
      loc.isHub
        ? 'border-[#A855F7] shadow-md ring-1 ring-[#A855F7]/25'
        : 'border-[#E5E7EB] hover:border-[#A855F7]'
    }`}
    data-testid={`location-card-${loc.slug}`}
  >
    {loc.isHub && (
      <div
        className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#A855F7] text-white text-[9px] font-black uppercase tracking-widest shadow-sm"
        data-testid={`location-card-${loc.slug}-hub-badge`}
      >
        Main Hub
      </div>
    )}
    <div className="relative h-40 overflow-hidden">
      <img
        src={resolveImageUrl(imageUrl) || loc.image}
        alt={`LGRA running classes at ${loc.name}`}
        width="400"
        height="160"
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-[1400ms] ease-out group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#4C1D95]/70 via-[#4C1D95]/20 to-transparent" aria-hidden />
      <div className="absolute top-3 left-3">
        <div className="inline-flex items-center gap-1 px-2 py-1 bg-white/95 text-[#7E22CE] text-[10px] font-black uppercase tracking-widest rounded">
          <MapPin size={11} /> {loc.name}
        </div>
      </div>
    </div>

    <div className="p-5 flex-1 flex flex-col">
      <div className="text-[10px] uppercase tracking-widest text-[#A855F7] font-black mb-1">
        {loc.subtitle}
      </div>
      <h3 className="text-xl font-black tracking-tight mb-3 text-slate-900">
        {loc.name}
      </h3>
      <ul className="space-y-1.5 mb-4 flex-1">
        {loc.programmes.map((p) => (
          <li key={p} className="flex items-start gap-2 text-sm text-slate-700">
            <Check size={14} className="text-[#A855F7] mt-0.5 flex-shrink-0" strokeWidth={3} />
            <span>{p}</span>
          </li>
        ))}
      </ul>
      <Link
        to={`/all-classes?location=${loc.slug}`}
        onClick={() => rememberLocation(loc.slug)}
        className="inline-flex items-center justify-center gap-2 h-11 rounded-md bg-[#A855F7] hover:bg-[#7E22CE] text-white font-black uppercase tracking-widest text-xs transition-colors"
        data-testid={`location-card-${loc.slug}-cta`}
      >
        {loc.cta} <ArrowRight size={14} />
      </Link>
    </div>
  </div>
);

const OtherLocationCard = ({ imageUrl }) => (
  <div
    className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#F3E8FF] to-white border-2 border-[#E9D5FF] hover:shadow-xl transition-all flex flex-col"
    data-testid="location-card-other"
  >
    <div className="relative h-40 overflow-hidden">
      <img
        src={resolveImageUrl(imageUrl) || 'https://images.unsplash.com/photo-1546519638-68e109498ffc?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80'}
        alt="Request LGRA running training at another location"
        width="400"
        height="160"
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-[1400ms] ease-out group-hover:scale-105 opacity-70"
      />
      <div className="absolute inset-0 bg-gradient-to-br from-[#7E22CE]/80 via-[#A855F7]/50 to-transparent" aria-hidden />
      <div className="absolute top-3 left-3">
        <div className="inline-flex items-center gap-1 px-2 py-1 bg-white/95 text-[#7E22CE] text-[10px] font-black uppercase tracking-widest rounded">
          <MessageCircle size={11} /> Custom Request
        </div>
      </div>
    </div>

    <div className="p-5 flex-1 flex flex-col">
      <div className="text-[10px] uppercase tracking-widest text-[#A855F7] font-black mb-1">
        Need Training Somewhere Else?
      </div>
      <h3 className="text-xl font-black tracking-tight mb-3 text-slate-900">
        Request Training at Your Preferred Location
      </h3>
      <p className="text-sm text-slate-700 mb-4 flex-1">
        Looking for training in another area? Request an LGRA group class,
        corporate or school programme, or private coaching session at a
        location convenient for you.
      </p>
      <div className="flex flex-col gap-2 mt-auto">
        <a
          href={OTHER_LOCATION_WA_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 h-11 rounded-md bg-[#A855F7] hover:bg-[#7E22CE] text-white font-black uppercase tracking-widest text-xs transition-colors"
          data-testid="location-card-other-cta"
        >
          <MessageCircle size={14} /> Request Another Location <ArrowRight size={14} />
        </a>
      </div>
    </div>
  </div>
);

const TrainingLocationsSection = () => {
  // Fetch the admin-managed image map on mount. On failure, hook the empty
  // object so cards fall back to their hardcoded `loc.image` default.
  const [imageMap, setImageMap] = useState({});
  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/public/location-images`)
      .then((res) => { if (alive) setImageMap(res.data || {}); })
      .catch(() => { if (alive) setImageMap({}); });
    return () => { alive = false; };
  }, []);

  return (
    <section
      id="locations"
      className="max-w-7xl mx-auto px-6 py-16 md:py-20 scroll-mt-20"
      data-testid="training-locations-section"
    >
      <div className="mb-10 text-center">
        <div className="text-[10px] uppercase tracking-widest text-[#A855F7] font-black mb-2">
          Our Training Locations
        </div>
        <h2 className="text-3xl md:text-5xl font-black tracking-tight">
          Training Locations &amp; Coaching Options
        </h2>
        <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">
          Coached classes in Kuala Lumpur, Shah Alam and Putrajaya — plus online training plans and private coaching wherever you run.
        </p>
      </div>

      {/* 4 hub cards + 1 request card. On desktop: 4-col row for hubs, then
          the request card spans the full width beneath. On mobile: cards
          stack vertically for easy reading. */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-5">
        {LOCATIONS.map((loc) => (
          <LocationCard key={loc.slug} loc={loc} imageUrl={imageMap[loc.slug]} />
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="rounded-2xl bg-[#0F172A] text-white p-6 flex flex-col" data-testid="location-card-online">
          <div className="text-[10px] uppercase tracking-widest text-[#C4B5FD] font-black mb-1">Anywhere in Malaysia</div>
          <h3 className="text-xl font-black tracking-tight mb-3">Online Training Plans &amp; Remote Coaching</h3>
          <p className="text-sm text-white/80 flex-1">Not near a venue? Get a personalised weekly plan with coach support delivered online, or book a private coach to come to you.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link to="/online-training-plan" className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-[#A855F7] hover:bg-[#7E22CE] text-white font-black uppercase tracking-widest text-xs" data-testid="location-card-online-cta">Online plans <ArrowRight size={14} /></Link>
            <Link to="/coaching/private" className="inline-flex items-center gap-2 h-11 px-5 rounded-md border border-white/30 hover:bg-white/10 text-white font-black uppercase tracking-widest text-xs" data-testid="location-card-private-cta">Private coaching</Link>
          </div>
        </div>
        <OtherLocationCard imageUrl={imageMap.other} />
      </div>

      <div className="text-center mt-6">
        <Link
          to="/all-classes"
          className="text-xs font-black uppercase tracking-widest text-[#7E22CE] hover:text-[#4C1D95] inline-flex items-center gap-1"
          data-testid="training-locations-view-all"
        >
          View All Locations &amp; Class Schedules <ArrowRight size={12} />
        </Link>
      </div>
    </section>
  );
};

export default TrainingLocationsSection;
